import {
  afterNextRender,
  Component,
  computed,
  effect,
  inject,
  Injector,
  OnInit,
  output,
  signal,
  viewChild,
  ChangeDetectionStrategy,
} from "@angular/core";
import { FormsModule, NgForm } from "@angular/forms";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { TableModule } from "@openng/optimus-ui/table";
import { CheckboxModule } from "@openng/optimus-ui/checkbox";
import { ButtonModule } from "@openng/optimus-ui/button";
import { MessageService } from "@openng/optimus-ui/api";
import { NotificationChannelSetting } from "../../../models/api/notification/notification-channel-settings";
import { NotificationsApiService } from "../../../services/api/notifications-api/notifications-api.service";
import { PermissionService } from "../../../services/api/permission/permission.service";

@Component({
  standalone: true,
  selector: "app-notifications-settings",
  imports: [TableModule, TranslateModule, CheckboxModule, FormsModule, ButtonModule],
  templateUrl: "./notifications-settings.component.html",
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: "./notifications-settings.component.scss",
})
export class NotificationsSettingsComponent implements OnInit {
  public readonly saved = output<void>();
  protected readonly settingsForm = viewChild<NgForm>("settingsForm");
  protected readonly settings = signal<NotificationChannelSetting[]>([]);
  protected readonly isLoading = signal(true);
  protected readonly isSaving = signal(false);
  protected readonly loadError = signal(false);
  protected readonly saveError = signal(false);
  protected readonly snapshotChanged = signal(false);
  private readonly notificationsApi = inject(NotificationsApiService);
  private readonly permissions = inject(PermissionService);
  protected readonly canUpdateSettings = computed(() =>
    this.permissions.hasPermission(this.permissions.allPermissions.NOTIFICATION_UPDATE)
  );
  private readonly message = inject(MessageService);
  private readonly translate = inject(TranslateService);
  private readonly injector = inject(Injector);
  private readonly formStateRevision = signal(0);
  protected readonly canSave = computed(() => {
    this.formStateRevision();
    const form = this.settingsForm();
    return this.canUpdateSettings() && !this.isSaving() && form?.dirty === true && form.valid;
  });

  constructor() {
    effect((onCleanup) => {
      const subscription = this.settingsForm()?.form.events.subscribe(() => {
        this.formStateRevision.update((revision) => revision + 1);
      });
      onCleanup(() => subscription?.unsubscribe());
    });
  }

  ngOnInit(): void {
    this.loadSettings();
  }

  protected retryLoad(): void {
    this.loadSettings();
  }

  protected save(): void {
    if (!this.canSave()) {
      return;
    }

    this.isSaving.set(true);
    this.saveError.set(false);
    this.snapshotChanged.set(false);
    const submittedSettings = this.copySettings(this.settings());

    this.notificationsApi.updateNotificationSettings(submittedSettings).subscribe({
      next: (settings) => {
        this.applyNotificationSettings(settings);
        this.isSaving.set(false);
        this.message.add({
          key: "global",
          severity: "success",
          summary: this.translate.instant("notification_settings.table.saved"),
          life: 3500,
        });
        this.saved.emit();
      },
      error: (error: unknown) => {
        this.isSaving.set(false);
        const snapshotIsStale =
          error !== null && typeof error === "object" && "expectedSnapshotChange" in error
            ? error.expectedSnapshotChange
            : false;
        if (snapshotIsStale) {
          this.settings.set([]);
          this.snapshotChanged.set(true);
          this.loadSettings(true);
          return;
        }
        this.saveError.set(true);
      },
    });
  }

  protected categoryTranslationKey(setting: NotificationChannelSetting): string {
    return `notifications.notification_templates.categories.${setting.category}`;
  }

  private loadSettings(preserveSnapshotMessage = false): void {
    this.isLoading.set(true);
    this.loadError.set(false);
    this.saveError.set(false);
    if (!preserveSnapshotMessage) {
      this.snapshotChanged.set(false);
    }

    this.notificationsApi.getNotificationSettings().subscribe({
      next: (settings) => {
        this.applyNotificationSettings(settings);
        this.isLoading.set(false);
        this.loadError.set(false);
      },
      error: () => {
        this.isLoading.set(false);
        this.loadError.set(true);
      },
    });
  }

  private applyNotificationSettings(settings: NotificationChannelSetting[]): void {
    const notificationSettings = this.copySettings(settings);
    this.settings.set(notificationSettings);

    afterNextRender(
      () => {
        if (this.settings() !== notificationSettings) {
          return;
        }

        this.settingsForm()?.resetForm(this.formValue(notificationSettings));
        this.formStateRevision.update((revision) => revision + 1);
      },
      { injector: this.injector }
    );
  }

  private formValue(settings: NotificationChannelSetting[]): Record<string, boolean> {
    return Object.fromEntries(
      settings.flatMap((setting) => [
        [`${setting.category}-app`, setting.app],
        [`${setting.category}-email`, setting.email],
      ])
    );
  }

  private copySettings(settings: NotificationChannelSetting[]): NotificationChannelSetting[] {
    return settings.map((setting) => ({ ...setting }));
  }
}
